type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Whether the sign-in screen may send the browser to Beyond Accounts by itself.
 *
 * A person who opens an address of the console signed out is sent to Accounts at once and comes back where
 * they were going. Two cases must not leave on their own: right after signing out, when Accounts would sign
 * the person straight back in, and right after a departure that came back without a session, when leaving
 * again would loop between the two services. Both wait for the person to choose to continue.
 */
export class Departures {
	static readonly #key = 'beyond.cdn.departure';
	/** Milliseconds after a departure during which the screen waits instead of leaving again */
	static readonly window = 60000;

	#storage?: Storage;
	#clock: () => number;

	constructor(storage?: Storage, clock: () => number = Date.now) {
		this.#storage = storage;
		this.#clock = clock;
	}

	/**
	 * true when the browser may leave for the authority without being asked
	 */
	get due(): boolean {
		let kept: string | null | undefined;
		try {
			kept = this.#storage?.getItem(Departures.#key);
		} catch {
			// Without storage there is no loop guard, so the person is asked
			return false;
		}
		if (!kept) return true;
		const when = Number(kept);
		return Number.isFinite(when) && this.#clock() - when > Departures.window;
	}

	/** Records a departure, automatic or chosen */
	leave() {
		this.#write(String(this.#clock()));
	}

	/** After signing out: the screen asks until the person chooses to sign in again */
	hold() {
		this.#write('held');
	}

	#write(value: string) {
		try {
			this.#storage?.setItem(Departures.#key, value);
		} catch {
			// The guard is a convenience: without it the person is asked
		}
	}
}
